import Markdown from '@/common/atoms/Markdown'
import { Box, BoxProps, Center, HStack, Text, Textarea } from '@chakra-ui/react'
import React, { forwardRef } from 'react'
import {
  ChecklistIcon,
  DecisionIcon,
  EditIcon,
  FileIcon,
  IndicatorsIcon,
  MeetingStepNextStepsIcon,
  MembersIcon,
  TaskIcon,
  ThreadIcon,
} from 'src/icons'
import { useTranslation } from 'react-i18next'
import { SummaryPiece } from '../meetingSummaryShareBoard'
import MeetingSummaryPieceFacts from './MeetingSummaryPieceFacts'

interface Props extends BoxProps {
  piece: SummaryPiece
  isDragging: boolean
  // Set when the piece is in « with adjustment »: its text becomes editable
  edited?: string
  onEdit?(body: string): void
}

// Each kind of piece with the icon the app already gives it, and a colour to
// tell them apart at a glance in a long list
type PieceKind =
  | 'summary'
  | 'attendees'
  | 'notes'
  | 'thread'
  | 'decision'
  | 'nextStep'
  | 'task'
  | 'roleReview'
  | 'checklist'
  | 'indicators'

const kinds: Record<PieceKind, { icon: React.ElementType; color: string }> = {
  summary: { icon: FileIcon, color: 'gray' },
  attendees: { icon: MembersIcon, color: 'cyan' },
  notes: { icon: EditIcon, color: 'purple' },
  thread: { icon: ThreadIcon, color: 'blue' },
  decision: { icon: DecisionIcon, color: 'orange' },
  nextStep: { icon: MeetingStepNextStepsIcon, color: 'teal' },
  task: { icon: TaskIcon, color: 'green' },
  // The review of the Projects step: that step's icon, as in the meeting
  roleReview: { icon: TaskIcon, color: 'green' },
  checklist: { icon: ChecklistIcon, color: 'yellow' },
  indicators: { icon: IndicatorsIcon, color: 'cyan' },
}

// One piece of the compte rendu, styled as the cards of the projects kanban
const MeetingSummaryShareCard = forwardRef<HTMLDivElement, Props>(
  function MeetingSummaryShareCard(
    { piece, isDragging, edited, onEdit, ...boxProps },
    ref
  ) {
    const { t } = useTranslation()
    const kindKey: PieceKind =
      piece.kind in kinds ? (piece.kind as PieceKind) : 'summary'
    const kind = kinds[kindKey]
    const Icon = kind.icon

    return (
      <Box
        ref={ref}
        p={3}
        bg="white"
        _dark={{ bg: 'blackAlpha.500' }}
        borderRadius="md"
        borderWidth={1}
        boxShadow={isDragging ? 'lg' : 'sm'}
        transition="box-shadow 200ms ease-out"
        _hover={{ boxShadow: 'md' }}
        {...boxProps}
      >
        <HStack align="start" spacing={2}>
          <Center
            flexShrink={0}
            w={7}
            h={7}
            borderRadius="full"
            bg={`${kind.color}.100`}
            color={`${kind.color}.700`}
            _dark={{ bg: `${kind.color}.800`, color: `${kind.color}.100` }}
          >
            <Icon size={16} />
          </Center>
          <Box minW={0}>
            <Text fontSize="xs" color="gray.500" _dark={{ color: 'gray.300' }}>
              {t(`MeetingSummaryShareModal.kinds.${kindKey}`)}
              {piece.section && ` · ${piece.section}`}
            </Text>
            <Text fontWeight="bold" fontSize="sm">
              {piece.title}
            </Text>
            <MeetingSummaryPieceFacts piece={piece} />
          </Box>
        </HStack>

        {edited !== undefined && onEdit ? (
          <Textarea
            mt={2}
            size="sm"
            value={edited}
            aria-label={t('MeetingSummaryShareModal.editPiece', {
              title: piece.title,
            })}
            onChange={(event) => onEdit(event.target.value)}
            // Typing must not start a drag
            onMouseDown={(event) => event.stopPropagation()}
          />
        ) : (
          piece.body && (
            <Box mt={1} fontSize="sm" noOfLines={4}>
              <Markdown>{piece.body}</Markdown>
            </Box>
          )
        )}
      </Box>
    )
  }
)

export default MeetingSummaryShareCard
